<template>
  <Transition name="toast">
    <div
      v-if="toastState.isVisible"
      class="fixed left-1/2 top-10 flex -translate-x-1/2 items-center justify-between gap-2 rounded-lg bg-primary-1000 p-4 shadow-md"
      role="alert"
      aria-hidden="true"
    >
      <component :is="getIcon" v-if="toastState.showIcon" class="h-6 w-6" :class="getIconClasses" :stroke="2.5" />
      <p class="text-sm text-primary-100" role="status" aria-live="polite">{{ toastState.message }}</p>

      <Button
        v-if="toastState.actionButton"
        type="outlined"
        aria-label="toast action"
        customStyle="ml-2"
        @click="toastState.actionButton.onClick"
      >
        {{ toastState.actionButton.label }}
      </Button>
      <Button
        v-if="toastState.dismissible"
        type="icon"
        aria-label="Close"
        data-testid="toast-close-button"
        customStyle="!p-0 ml-2 bg-transparent"
        @click="hideToast"
      >
        <IconX class="w-4 text-primary-100" />
      </Button>
    </div>
  </Transition>
</template>

<script setup>
import { IconX, IconAlertCircle, IconInfoCircle, IconCircleCheck } from '@tabler/icons-vue';
import { computed } from 'vue';

import Button from './Button.vue';

import { useToast } from '@/hooks/useToast';

const { toastState, hideToast } = useToast();

const getIcon = computed(() => {
  switch (toastState.type) {
    case 'success':
      return IconCircleCheck;
    case 'error':
      return IconAlertCircle;
    case 'warning':
      return IconAlertCircle;
    case 'info':
      return IconInfoCircle;
    default:
      return IconInfoCircle;
  }
});
const getIconClasses = computed(() => {
  switch (toastState.type) {
    case 'success':
      return 'text-green-500';
    case 'error':
      return 'text-red-500';
    case 'warning':
      return 'text-yellow-500';
    case 'info':
      return 'text-blue-500';
    default:
      return 'text-blue-500';
  }
});
</script>

<style>
@keyframes slideInDown {
  from {
    transform: translate(-50%, -50px) scaleY(0.8);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    transform: translate(-50%, 0) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, 0) scale(0.9);
  }
}

.toast-enter-active {
  animation: slideInDown 0.5s cubic-bezier(0, 1.5, 0.5, 1);
}

.toast-leave-active {
  animation: fadeOut 0.15s linear forwards;
}
</style>
